import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Package, DollarSign, Hash, Tag, Box, Percent, Star, Calendar,
  Camera, Upload, Barcode, X, Save, Ban, Edit3
} from 'lucide-react';

const categories = [
  'Pan Masala', 'Gutka', 'Supari', 'Mouth Freshener', 'Cigarettes', 'Beverages', 'Snacks', 'Other'
];

const units = ['pieces', 'kg', 'boxes', 'bundles', 'packets', 'liters', 'grams'];

const initialForm = {
  name: '',
  category: '',
  unit: 'pieces',
  purchasePrice: '',
  salePrice: '',
  piecesPerPack: '1',
  packsPerBox: '1',
  pricePerHalfPack: '',
  pricePerFullPack: '',
  pricePerBox: '',
  stock: '',
  minStock: '',
  supplierName: '',
  supplierContact: '',
  expiryDate: '',
  barcode: '',
  taxRate: '',
  description: '',
  image: null,
};

export default function ProductForm({ product, onSubmit, onCancel, loading = false }) {
  const isEdit = !!product;
  const [form, setForm] = useState(
    product
      ? { ...initialForm, ...product, purchasePrice: product.purchasePrice || '', salePrice: product.salePrice || '' }
      : initialForm
  );
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(product?.image || null);

  const profitMargin = useMemo(() => {
    const cost = parseFloat(form.purchasePrice) || 0;
    const sale = parseFloat(form.salePrice) || 0;
    if (cost === 0 || sale === 0) return 0;
    return ((sale - cost) / sale) * 100;
  }, [form.purchasePrice, form.salePrice]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Product name is required';
    if (!form.salePrice || parseFloat(form.salePrice) <= 0) errs.salePrice = 'Valid sale price is required';
    if (form.stock === '' || parseInt(form.stock) < 0) errs.stock = 'Valid stock quantity is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit?.({
      ...form,
      purchasePrice: parseFloat(form.purchasePrice) || 0,
      salePrice: parseFloat(form.salePrice) || 0,
      piecesPerPack: parseInt(form.piecesPerPack) || 1,
      packsPerBox: parseInt(form.packsPerBox) || 1,
      pricePerHalfPack: parseFloat(form.pricePerHalfPack) || 0,
      pricePerFullPack: parseFloat(form.pricePerFullPack) || 0,
      pricePerBox: parseFloat(form.pricePerBox) || 0,
      stock: parseInt(form.stock) || 0,
      minStock: parseInt(form.minStock) || 0,
      taxRate: parseFloat(form.taxRate) || 0,
    });
  };

  const inputClass = (field) => `
    w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border rounded-lg
    text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400
    transition-colors
    ${errors[field] ? 'border-red-500 dark:border-red-400' : 'border-gray-200 dark:border-gray-600'}
  `;

  const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          {isEdit ? <Edit3 size={20} /> : <Package size={20} />}
          {isEdit ? 'Edit Product' : 'Add Product'}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4 md:col-span-2">
          <label className={labelClass}>Product Image</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <Camera size={24} className="text-gray-400" />
              )}
            </div>
            <label className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
              <Upload size={15} />
              Upload Image
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
            {imagePreview && (
              <button
                type="button"
                onClick={() => { setImagePreview(null); setForm((prev) => ({ ...prev, image: null })); }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div>
          <label className={labelClass}>Product Name *</label>
          <div className="relative">
            <Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Product name"
              className={inputClass('name')}
            />
          </div>
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>

        <div>
          <label className={labelClass}>Category</label>
          <select
            value={form.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500"
          >
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Unit</label>
          <select
            value={form.unit}
            onChange={(e) => handleChange('unit', e.target.value)}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500"
          >
            {units.map((u) => (
              <option key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Barcode</label>
          <div className="relative">
            <Barcode size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={form.barcode}
              onChange={(e) => handleChange('barcode', e.target.value)}
              placeholder="Enter barcode"
              className={inputClass('barcode')}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Tax Rate (%)</label>
          <div className="relative">
            <Percent size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.taxRate}
              onChange={(e) => handleChange('taxRate', e.target.value)}
              placeholder="0"
              className={inputClass('taxRate')}
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Packaging Config</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Pieces per Pack</label>
              <div className="relative">
                <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  min="1"
                  value={form.piecesPerPack}
                  onChange={(e) => handleChange('piecesPerPack', e.target.value)}
                  placeholder="e.g. 20"
                  className={inputClass('piecesPerPack')}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Packs per Box</label>
              <div className="relative">
                <Box size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  min="1"
                  value={form.packsPerBox}
                  onChange={(e) => handleChange('packsPerBox', e.target.value)}
                  placeholder="e.g. 20"
                  className={inputClass('packsPerBox')}
                />
              </div>
            </div>
          </div>
          {parseInt(form.piecesPerPack) > 0 && parseInt(form.packsPerBox) > 0 && (
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              1 Box = {parseInt(form.packsPerBox)} Packs = {parseInt(form.piecesPerPack) * parseInt(form.packsPerBox)} Pieces
            </p>
          )}
        </div>

        <div>
          <label className={labelClass}>Purchase Price</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              step="0.01"
              value={form.purchasePrice}
              onChange={(e) => handleChange('purchasePrice', e.target.value)}
              placeholder="0.00"
              className={inputClass('purchasePrice')}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Price per Piece *</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              step="0.01"
              value={form.salePrice}
              onChange={(e) => handleChange('salePrice', e.target.value)}
              placeholder="0.00"
              className={inputClass('salePrice')}
            />
          </div>
          {errors.salePrice && <p className="mt-1 text-xs text-red-500">{errors.salePrice}</p>}
        </div>

        <div>
          <label className={labelClass}>Price per Half Pack</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.pricePerHalfPack}
              onChange={(e) => handleChange('pricePerHalfPack', e.target.value)}
              placeholder="0.00"
              className={inputClass('pricePerHalfPack')}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Price per Full Pack</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.pricePerFullPack}
              onChange={(e) => handleChange('pricePerFullPack', e.target.value)}
              placeholder="0.00"
              className={inputClass('pricePerFullPack')}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Price per Box (Optional)</label>
          <div className="relative">
            <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.pricePerBox}
              onChange={(e) => handleChange('pricePerBox', e.target.value)}
              placeholder="0.00"
              className={inputClass('pricePerBox')}
            />
          </div>
        </div>

        {profitMargin > 0 && (
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Star size={15} className="text-amber-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Profit Margin:</span>
              <span className={`text-sm font-bold ${profitMargin >= 30 ? 'text-emerald-600' : profitMargin >= 15 ? 'text-amber-500' : 'text-red-500'}`}>
                {profitMargin.toFixed(1)}%
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                (Cost: Rs. {parseFloat(form.purchasePrice) || 0} | Sale: Rs. {parseFloat(form.salePrice) || 0})
              </span>
            </div>
          </div>
        )}

        <div>
          <label className={labelClass}>Stock Quantity *</label>
          <div className="relative">
            <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              min="0"
              value={form.stock}
              onChange={(e) => handleChange('stock', e.target.value)}
              placeholder="0"
              className={inputClass('stock')}
            />
          </div>
          {errors.stock && <p className="mt-1 text-xs text-red-500">{errors.stock}</p>}
        </div>

        <div>
          <label className={labelClass}>Minimum Stock Level</label>
          <div className="relative">
            <Box size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="number"
              min="0"
              value={form.minStock}
              onChange={(e) => handleChange('minStock', e.target.value)}
              placeholder="0"
              className={inputClass('minStock')}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Expiry Date</label>
          <div className="relative">
            <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={form.expiryDate}
              onChange={(e) => handleChange('expiryDate', e.target.value)}
              className={inputClass('expiryDate')}
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Supplier Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={form.supplierName}
                onChange={(e) => handleChange('supplierName', e.target.value)}
                placeholder="Supplier name"
                className={inputClass('supplierName')}
              />
            </div>
            <div className="relative">
              <Box size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={form.supplierContact}
                onChange={(e) => handleChange('supplierContact', e.target.value)}
                placeholder="Supplier contact"
                className={inputClass('supplierContact')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <Ban size={16} />
            Cancel
          </button>
        )}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save size={16} />
          )}
          {isEdit ? 'Update Product' : 'Save Product'}
        </motion.button>
      </div>
    </motion.form>
  );
}
